import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, Button, Paper, Alert, FormControl, InputLabel, Select, MenuItem, CircularProgress } from '@mui/material';
import GenericForm from '../components/GenericForm.js';
import { jobFormFields as staticJobFormFields } from '../forms/jobFormFields.js';
import { useAuth } from '../AuthContext.js';

const CreateJob = () => {
  const { user } = useAuth();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({ categories: [] });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(isEditMode);
  const navigate = useNavigate();

  const [jobFormFields, setJobFormFields] = useState(staticJobFormFields);
  const [availableBusinesses, setAvailableBusinesses] = useState([]);

  useEffect(() => {
    const promises = [axios.get('/api/categories')];
    if (user.role === 'admin') {
      promises.push(axios.get('/api/admin/businesses'));
    }
    if (isEditMode) {
      promises.push(axios.get(`/api/jobs/${id}`));
    }

    Promise.all(promises)
      .then(results => {
        const categoriesData = results[0].data;
        let resultIndex = 1;

        const updatedFields = staticJobFormFields.map(field => {
            if (field.name === 'categories') {
                return { ...field, options: categoriesData };
            }
            return field;
        });
        setJobFormFields(updatedFields);

        if (user.role === 'admin') {
          setAvailableBusinesses(results[resultIndex]?.data || []);
          resultIndex++;
        }
        if (isEditMode) {
          const jobData = results[resultIndex]?.data || null;
          if (jobData) {
            const selectedCategories = jobData.categories.map(c => {
                const fullCategory = categoriesData.find(cat => cat.name === c.category.name);
                return fullCategory ? { id: fullCategory.id, name: fullCategory.name } : null;
            }).filter(Boolean);
            setFormData({ ...jobData, categories: selectedCategories });
          }
        }
      })
      .catch(err => {
        console.error("Errore nel caricare i dati iniziali:", err);
        setError("Impossibile caricare i dati necessari per il form.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, isEditMode, user.role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // --- INIZIO DELLA MODIFICA CHIAVE ---
    // Invece di inviare tutto il 'formData', costruiamo un oggetto pulito
    // con solo i campi che il backend si aspetta per una creazione/modifica.
    const dataToSend = {
      title: formData.title,
      description: formData.description,
      location: formData.location,
      province: formData.province,
      type: formData.type,
      requirements: formData.requirements,
      salary: formData.salary,
      categories: formData.categories.map(cat => cat.id)
    };
    // Se l'utente è un admin e sta creando un nuovo annuncio, aggiungiamo il businessId
    if (user.role === 'admin' && !isEditMode) {
        dataToSend.businessId = formData.businessId;
    }
    // --- FINE DELLA MODIFICA CHIAVE ---

    try {
      if (isEditMode) {
        await axios.put(`/api/jobs/${id}`, dataToSend);
      } else {
        const url = user.role === 'admin' ? '/api/admin/jobs' : '/api/jobs';
        await axios.post(url, dataToSend);
      }
      navigate('/'); // Reindirizzamento corretto
    } catch (err) {
      setError(err.response?.data?.error || 'Si è verificato un errore imprevisto.');
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>;
  }
  
  return (
    <Paper sx={{ p: 4, maxWidth: 800, margin: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        {isEditMode ? 'Modifica Opportunità' : 'Pubblica una Nuova Opportunità'}
      </Typography>
      <Box component="form" onSubmit={handleSubmit}>
        {user.role === 'admin' && (
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="business-select-label">Seleziona Azienda</InputLabel>
            <Select
              labelId="business-select-label"
              name="businessId"
              value={formData.businessId || ''}
              label="Seleziona Azienda"
              onChange={(e) => setFormData(prev => ({ ...prev, businessId: e.target.value }))}
              required
              disabled={isEditMode}
            >
              {availableBusinesses.map(biz => (
                <MenuItem key={biz.id} value={biz.id}>{biz.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <GenericForm
          fields={jobFormFields}
          formData={formData}
          setFormData={setFormData}
        />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        <Button type="submit" variant="contained" size="large" sx={{ mt: 3 }}>
          {isEditMode ? 'Salva Modifiche' : 'Pubblica Annuncio'}
        </Button>
      </Box>
    </Paper>
  );
};

export default CreateJob;